import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, ArrowUp, ArrowDown, Zap, Sparkles, AlertTriangle, ExternalLink, X, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LAST_SEEN_KEY = "notifications:tier-changes:last-seen";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function NotificationsBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [missing, setMissing] = useState([]);
  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem(LAST_SEEN_KEY) || "");
  const ref = useRef(null);

  const isAdmin = user?.role === "admin" || user?.role === "dept_lead";

  const load = () => {
    api
      .get("/notifications/tier-changes", { params: { limit: 60 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
    if (isAdmin) {
      api
        .get("/shopify/missing-orders", { params: { days: 7 } })
        .then((r) => setMissing(r.data.missing || []))
        .catch(() => setMissing([]));
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  const unreadCount = useMemo(() => {
    const missingCount = missing.length;
    if (!items.length) return missingCount;
    if (!lastSeen) return items.length + missingCount;
    return items.filter((i) => i.at > lastSeen).length + missingCount;
  }, [items, lastSeen, missing]);

  const openPanel = () => {
    setOpen(true);
    if (items.length && items[0].at !== lastSeen) {
      const newSeen = items[0].at;
      localStorage.setItem(LAST_SEEN_KEY, newSeen);
      setLastSeen(newSeen);
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-bell"
        onClick={() => (open ? setOpen(false) : openPanel())}
        className="relative inline-flex items-center justify-center h-10 w-10 rounded-full border border-border/60 bg-card hover:bg-secondary/60 transition-colors"
        title="Tier progression notifications"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center px-1 ring-2 ring-background"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-[380px] max-h-[520px] rounded-2xl border border-border/60 bg-card shadow-2xl z-50 flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <div>
                <div className="text-sm font-semibold">Tier Progression</div>
                <div className="text-[11px] text-muted-foreground">
                  {items.length ? `${items.length} recent event${items.length === 1 ? "" : "s"}` : "No events yet"}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-y-auto">
            {isAdmin && missing.length > 0 && (
              <div className="border-b border-border/40">
                <div className="px-4 py-3 flex items-center gap-2 bg-amber-500/5">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                      {missing.length} missing Shopify order{missing.length === 1 ? "" : "s"}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Click to open in Shopify · edit &amp; save the order to force webhook resync
                    </div>
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {missing.slice(0, 20).map((m) => (
                    <div
                      key={m.order_number}
                      className="flex items-center justify-between gap-2 px-4 py-2 border-b border-border/40 last:border-b-0 hover:bg-amber-500/5 transition-colors group"
                    >
                      <a
                        href={m.admin_search_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`missing-order-${m.order_number}`}
                        className="flex-1 flex items-center gap-3 min-w-0"
                      >
                        <div className="text-sm font-mono">{m.order_number}</div>
                        <div className="flex-1 text-[11px] text-muted-foreground truncate">
                          between {m.surrounding_context.prev} ({m.surrounding_context.prev_date}) &amp; {m.surrounding_context.next} ({m.surrounding_context.next_date})
                        </div>
                        <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                      </a>
                      <button
                        data-testid={`dismiss-missing-${m.order_number}`}
                        onClick={async () => {
                          await api.post("/shopify/missing-orders/dismiss", { order_number: m.order_number });
                          setMissing((prev) => prev.filter((x) => x.order_number !== m.order_number));
                        }}
                        title="Not an affiliate order — dismiss"
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-600 p-1 rounded"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {missing.length > 20 && (
                    <div className="px-4 py-2 text-[11px] text-muted-foreground text-center">
                      +{missing.length - 20} more
                    </div>
                  )}
                </div>
              </div>
            )}
            {items.length === 0 && missing.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                All caught up — no missing orders or tier changes.
              </div>
            )}
            {items.map((n) => (
              <NotificationRow key={n.id} n={n} onNavigate={() => setOpen(false)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({ n, onNavigate }) {
  const isFastTrack = n.kind?.startsWith("fast_track_");
  const isFastTrackAchieved = n.kind === "fast_track_achieved";
  const isFastTrackMissed = n.kind === "fast_track_missed";
  const isActivation = n.kind === "activation";
  const isUp = n.direction === "up";
  const isDown = n.direction === "down";
  const isCreated = !n.from_tier && !isFastTrack && !isActivation;

  let Icon = Sparkles;
  let accent = "text-primary bg-primary/10";
  if (isActivation) { Icon = Rocket; accent = "text-orange-600 bg-orange-500/10"; }
  else if (isFastTrackAchieved) { Icon = Zap; accent = "text-emerald-600 bg-emerald-500/10"; }
  else if (isFastTrackMissed) { Icon = Zap; accent = "text-neutral-500 bg-neutral-500/10"; }
  else if (isUp) { Icon = ArrowUp; accent = "text-emerald-600 bg-emerald-500/10"; }
  else if (isDown) { Icon = ArrowDown; accent = "text-rose-600 bg-rose-500/10"; }

  return (
    <Link
      to={`/affiliates/${n.affiliate_id}`}
      onClick={onNavigate}
      data-testid={`notification-row-${n.id}`}
      className="block px-4 py-3 border-b border-border/40 last:border-b-0 hover:bg-secondary/40 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          {isActivation ? (
            <div className="text-sm">
              <span className="font-semibold">{n.affiliate_name}</span>
              <span className="text-muted-foreground"> activated and generated a sale of </span>
              <span className="font-semibold text-orange-600">₹{Number(n.amount || 0).toLocaleString("en-IN")}</span>
              {n.is_new_customer && (
                <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-600">· NEW</span>
              )}
            </div>
          ) : isFastTrack ? (
            <div className="text-sm">
              <span className="font-semibold">{n.affiliate_name}</span>
              {isFastTrackAchieved ? (
                <>
                  <span className="text-muted-foreground"> unlocked </span>
                  <span className="font-semibold text-emerald-600">Fast Track Bonus</span>
                  <span className="text-muted-foreground"> with </span>
                  <span className="font-medium">{n.new_customers} new customers</span>
                </>
              ) : (
                <>
                  <span className="text-muted-foreground"> missed Fast Track (only </span>
                  <span className="font-medium">{n.new_customers}/2</span>
                  <span className="text-muted-foreground"> new customers in 30d)</span>
                </>
              )}
            </div>
          ) : (
          <div className="text-sm">
            <span className="font-semibold">{n.affiliate_name}</span>
            {isCreated ? (
              <span className="text-muted-foreground"> was added at </span>
            ) : isUp ? (
              <span className="text-muted-foreground"> promoted from </span>
            ) : isDown ? (
              <span className="text-muted-foreground"> demoted from </span>
            ) : (
              <span className="text-muted-foreground"> tier change from </span>
            )}
            {n.from_tier && (
              <>
                <span className="font-medium">{n.from_tier}</span>
                <span className="text-muted-foreground"> → </span>
              </>
            )}
            <span className={`font-semibold ${isUp ? "text-emerald-600" : isDown ? "text-rose-600" : "text-primary"}`}>
              {n.to_tier}
            </span>
          </div>
          )}
          {isFastTrackAchieved && n.reward_mrp > 0 && (
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest bg-emerald-500/10 text-emerald-600 rounded-full px-2 py-0.5">
              <Zap className="h-3 w-3" /> ₹{n.reward_mrp.toLocaleString("en-IN")} MRP reward
            </div>
          )}
          {!isFastTrack && n.unlock_reward_mrp > 0 && (
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest bg-primary/10 text-primary rounded-full px-2 py-0.5">
              <Zap className="h-3 w-3" /> ₹{n.unlock_reward_mrp.toLocaleString("en-IN")} MRP unlock bonus
            </div>
          )}
          {n.reason && !n.reason.startsWith("Affiliate created") && (
            <div className="mt-1 text-[11px] text-muted-foreground truncate">{n.reason}</div>
          )}
          <div className="mt-1 text-[11px] text-muted-foreground">{timeAgo(n.at)}</div>
        </div>
      </div>
    </Link>
  );
}
